import React, { useMemo } from "react";
import { Row } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import {
	ProductMetaInfo,
	AmazonFilterProps,
	AmazonCatalogFilterKeys,
	AmazonProductAppliedFilters
} from "@/Contracts/Products";
import ZButton from "@atoms/ZButton";
import ZSelect from "@atoms/ZSelect";
import { ZCheckbox } from "@atoms";
import { ZSpin } from "@molecules";
import { EIcons } from "@atoms/ZIcon/interface";
import ZPopover from "@atoms/ZPopover";
import ZImage from "@atoms/ZImage";
import amazonPrimeLogo from "config/assets/Logos/Prime.png";
import "./ItemFiltersMenu.less";

export interface AmazonItemFiltersMenuProps {
	metaInfo: ProductMetaInfo;
	initialMetaInfo?: ProductMetaInfo;
	amazonFilterState: AmazonFilterProps;
	amazonAppliedFilters?: AmazonProductAppliedFilters;
	onApply: () => void;
	onFilterChange: (values: string[]) => (filterKey: string) => void;
	onClearFilters: () => void;
	onFreeShippingChange: (e: CheckboxChangeEvent) => void;
	onPrimeEligibleChange: (e: CheckboxChangeEvent) => void;
	isVisible: boolean;
	onVisibleChange: (visible: boolean) => void;
	loading?: boolean;
}

// AmazonItemFiltersMenu
const AmazonItemFiltersMenu: React.FC<AmazonItemFiltersMenuProps> = props => {
	const {
		metaInfo,
		amazonFilterState,
		amazonAppliedFilters,
		onApply,
		onFilterChange,
		onClearFilters,
		onFreeShippingChange,
		onPrimeEligibleChange,
		isVisible,
		onVisibleChange,
		loading = false
	} = props;

	const amazonRefineMentsFilters = useMemo(() => {
		return metaInfo?.amazon_refinements;
	}, [metaInfo]);

	const getCategoryOptions = useMemo(() => {
		const availableOptions =
			(metaInfo.categories?.length
				? metaInfo.categories
				: amazonAppliedFilters?.categories) || [];

		const selectedValue = amazonFilterState.amazonProductCategory?.[0];
		if (!selectedValue) return availableOptions;

		const isSelectedInOptions = availableOptions.some(
			opt => opt.value === selectedValue
		);
		if (isSelectedInOptions) return availableOptions;

		const selectedOption = amazonAppliedFilters?.categories.find(
			opt => opt.value === selectedValue
		);
		if (selectedOption) {
			return [selectedOption, ...availableOptions];
		}

		return availableOptions;
	}, [metaInfo, amazonAppliedFilters, amazonFilterState]);

	const getSubCategoryOptions = useMemo(() => {
		const availableOptions =
			(metaInfo.sub_categories?.length
				? metaInfo.sub_categories
				: amazonAppliedFilters?.sub_categories) || [];

		const selectedValue = amazonFilterState.amazonProductSubCategory?.[0];
		if (!selectedValue) return availableOptions;

		const isSelectedInOptions = availableOptions.some(
			opt => opt.value === selectedValue
		);
		if (isSelectedInOptions) return availableOptions;

		const selectedOption = amazonAppliedFilters?.sub_categories.find(
			opt => opt.value === selectedValue
		);
		if (selectedOption) {
			return [selectedOption, ...availableOptions];
		}

		return availableOptions;
	}, [metaInfo, amazonAppliedFilters, amazonFilterState]);

	return (
		<ZPopover
			trigger="click"
			visible={isVisible}
			onVisibleChange={onVisibleChange}
			overlayClassName="item-filter-popover"
			placement="bottomLeft"
			size="sm"
			content={
				<ZSpin spinning={loading}>
					<Row className="amazon-item-filter">
						<Row className="main-menu">
							{/* Product Category */}
							<div className="filter-container">
								<span className="filter-title">
									Product Category
								</span>
								<div className="filter amazon-filter">
									<ZSelect
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										placeholder="Select Product Category"
										options={getCategoryOptions}
										value={
											amazonFilterState
												.amazonProductCategory?.[0] ||
											undefined
										}
										onChange={(value: any) => {
											onFilterChange(
												Array.isArray(value)
													? value
													: [value]
											)(
												AmazonCatalogFilterKeys.FilterByProductCategory
											);
										}}
										allowClear
									/>
								</div>
							</div>

							{/* Sub Category */}
							{(!!metaInfo.sub_categories?.length ||
								!!amazonAppliedFilters?.sub_categories
									?.length) && (
								<div className="filter-container">
									<span className="filter-title">
										Sub Category
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter Sub Category"
											options={getSubCategoryOptions}
											value={
												amazonFilterState
													.amazonProductSubCategory?.[0] ||
												undefined
											}
											onChange={(value: any) =>
												onFilterChange(
													Array.isArray(value)
														? value
														: [value]
												)(
													AmazonCatalogFilterKeys.FilterBySubCategory
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}

							{/* Delivery Day */}
							{!!amazonRefineMentsFilters?.delivery_day_options
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Delivery Day
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter delivery day"
											options={
												amazonRefineMentsFilters?.delivery_day_options ??
												[]
											}
											value={
												amazonFilterState
													.amazonProductDeliveryDay?.[0] ||
												undefined
											}
											onChange={(value: any) =>
												onFilterChange(
													Array.isArray(value)
														? value
														: [value]
												)(
													AmazonCatalogFilterKeys.FilterByDeliveryDay
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}

							{/* Availability */}
							{(!!amazonRefineMentsFilters?.availability_options
								?.length ||
								!!amazonAppliedFilters?.availability_options
									?.length) && (
								<div className="filter-container">
									<span className="filter-title">
										Availability
									</span>
									<div className="filter amazon-filter">
										<ZSelect
											getPopupContainer={trigger =>
												trigger.parentNode
											}
											placeholder="Enter availability"
											options={
												amazonRefineMentsFilters
													?.availability_options
													?.length
													? amazonRefineMentsFilters?.availability_options
													: amazonAppliedFilters?.availability_options
											}
											value={
												amazonFilterState
													.includeOutOfStockProducts?.[0] ||
												undefined
											}
											onChange={(value: any) =>
												onFilterChange(
													Array.isArray(value)
														? value
														: [value]
												)(
													AmazonCatalogFilterKeys.FilterByAvailability
												)
											}
											allowClear
										/>
									</div>
								</div>
							)}

							{/* Free Shipping */}
							{!!amazonRefineMentsFilters
								?.eligible_for_free_shipping_options
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Shipping
									</span>
									<div className="filter amazon-filter">
										<ZCheckbox
											checked={
												amazonFilterState.eligibleForFreeShipping
											}
											onChange={onFreeShippingChange}
											className="limit-to-distributor"
										>
											Eligible For Free Shipping
										</ZCheckbox>
									</div>
								</div>
							)}

							{/* Prime Eligible */}
							{!!amazonRefineMentsFilters?.prime_eligible
								?.length && (
								<div className="filter-container">
									<span className="filter-title">
										Prime Eligible
									</span>
									<div className="filter amazon-filter">
										<ZCheckbox
											checked={
												amazonFilterState.amazonPrimeEligible
											}
											onChange={onPrimeEligibleChange}
											className="limit-to-distributor"
										>
											<ZImage
												width={57}
												height={16}
												preview={false}
												alt="Prime Tag"
												wrapperClassName="pt-0.5"
												src={amazonPrimeLogo}
											/>
										</ZCheckbox>
									</div>
								</div>
							)}
						</Row>

						{/* Action Buttons */}
						<div className="mt-3 w-39 ml-auto flex justify-between">
							<ZButton onClick={onClearFilters} type="default">
								Clear
							</ZButton>
							<ZButton onClick={onApply} type="primary">
								Apply
							</ZButton>
						</div>
					</Row>
				</ZSpin>
			}
		>
			<ZButton iconName={EIcons.FILTER} type="primary" ghost size="small">
				Add Filters
			</ZButton>
		</ZPopover>
	);
};

export default AmazonItemFiltersMenu;
